'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
interface Service {
  id: string;
  icon: string;
  title: string;
  description: string;
  features: string[];
  image: string;
  imageAlt: string;
}

export default function ServicesList() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services: Service[] = [
  {
    id: 'service_support',
    icon: 'LifebuoyIcon',
    title: 'IT Support',
    description:
    'Comprehensive 24/7 technical support and maintenance for your entire business infrastructure. Keep your systems running smoothly with our expert team.',
    features: [
    'Help Desk Support',
    'Network Management',
    'Hardware Maintenance',
    'System Monitoring'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_142f78a81-1768650665382.png",
    imageAlt: 'IT support specialist providing technical assistance at computer workstation'
  },
  {
    id: 'service_web',
    icon: 'GlobeAltIcon',
    title: 'Web Design',
    description:
    'Custom, responsive websites designed to engage your audience and drive conversions. Beautiful design meets powerful functionality.',
    features: [
    'Responsive Design',
    'UI/UX Optimization',
    'E-commerce Solutions',
    'CMS Integration'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15ad10e97-1769166728845.png",
    imageAlt: 'Web designer working on responsive website layouts on multiple devices'
  },
  {
    id: 'service_dev',
    icon: 'CodeBracketIcon',
    title: 'Software Development',
    description:
    'Bespoke software solutions tailored to your unique business requirements. From concept to deployment, we build applications that scale.',
    features: [
    'Custom Applications',
    'API Development',
    'Database Design',
    'Mobile Apps'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfbc27cd-1766500363073.png",
    imageAlt: 'Software developers collaborating on code with multiple monitors displaying programming interfaces'
  },
  {
    id: 'service_security',
    icon: 'ShieldCheckIcon',
    title: 'Cybersecurity',
    description:
    'Protect your digital assets with comprehensive security solutions. From threat detection to compliance, we keep your business secure.',
    features: [
    'Security Audits',
    'Threat Detection',
    'Data Protection',
    'Compliance Management'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f3dbb3fd-1764669639747.png",
    imageAlt: 'Cybersecurity analyst monitoring network security dashboard with threat detection systems'
  },
  {
    id: 'service_cloud',
    icon: 'CloudIcon',
    title: 'Cloud Services',
    description:
    'Scalable cloud infrastructure and migration services. Leverage the power of cloud computing to transform your business operations.',
    features: [
    'Cloud Migration',
    'Infrastructure Setup',
    'Backup Solutions',
    'Cost Optimization'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b38f162-1767683091191.png",
    imageAlt: 'Modern cloud computing data center with illuminated server racks and networking equipment'
  }];


  return (
    <section className="py-20 lg:py-32 relative z-10 bg-gray-950">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="space-y-24">
          {services.map((service, index) =>
          <div
            key={service.id}
            id={service.id.replace('service_', '')}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center animate-on-scroll animate-in-delay-${
            index % 3 + 1} ${
            index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>

              {/* Image */}
              <div
              className={`image-zoom aspect-[4/3] bg-gray-800 rounded-xl border border-zinc-700 ${
              index % 2 === 1 ? 'lg:col-start-2' : ''}`
              }>

                <AppImage
                src={service.image}
                alt={service.imageAlt}
                className="w-full h-full object-cover" />

              </div>

              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                <div className="w-14 h-14 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 mb-6">
                  <Icon name={service.icon as any} size={28} />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {service.title}
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed mb-8">
                  {service.description}
                </p>

                <div className="mb-8">
                  <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-4">
                    Key Features
                  </h3>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) =>
                  <li key={`${service.id}_feature_${idx}`} className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                        <span className="text-gray-300">{feature}</span>
                      </li>
                  )}
                  </ul>
                </div>

                <Link
                href="/contact"
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors">

                  <span>Learn More</span>
                  <Icon name="ArrowRightIcon" size={18} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}